/**
 * Parser for /historian deep-link query strings (djin-cloud#181, widened by #806).
 *
 * Two independent URL forms are supported:
 *
 *   1. The LEGACY form (`site=`, `metrics=`, `device_ids=`, ...) — daily-ops
 *      report PDFs embed chart-footer links pre-scoped to a gas-day window.
 *      `parseHistorianUrlParams` turns that query string into a seed that
 *      expands to a site × devices × metrics cross-product.
 *   2. The `series=`/`q=` form (djin-cloud#806) — the shareable-URL contract
 *      the Historian page itself writes via `writeHistorianUrl`. It names
 *      exact site:device:metric triples (or just a persisted `queryId`)
 *      rather than a cross-product, so it round-trips a specific selection
 *      exactly.
 *
 * Both are pure functions, kept React-free so they are unit-testable in
 * isolation. `HistorianContext.seedFromUrl` is what turns either shape into
 * live provider state.
 */

import { EXPLICIT_INTERVALS, type IntervalOption } from '@/components/historian/historianConstants';

const VALID_INTERVALS = ['1m', '5m', '15m', '1h', '1d'];

export interface HistorianSeed {
  siteId: string;
  metrics: string[];
  deviceIds: string[];
  assetType: string;
  startIso: string | null;
  endIso: string | null;
  interval: string | null;
}

/**
 * The `series=`/`q=` deep-link seed.
 *
 * `series` and `queryId` are independently optional at the type level, but
 * `parseHistorianSeriesUrl` never returns both empty — it returns `null`
 * instead (there is nothing meaningful to seed from).
 */
export interface HistorianSeriesSeed {
  series: Array<{ siteId: string; deviceId: string; metric: string }>;
  startIso: string | null;
  endIso: string | null;
  interval: IntervalOption | null;
  queryId: string | null;
}

function parseCsv(raw: string | null): string[] {
  if (!raw) return [];
  return raw.split(',').map((s) => s.trim()).filter((s) => s.length > 0);
}

/**
 * ⚠️ Canonicalizes to `Date.toISOString()` rather than passing the raw input
 * through. The only production deep-link generator (`writeHistorianUrl`)
 * always emits that canonical form, so this is a no-op for every real link
 * and only normalizes a hand-typed or hand-edited URL (e.g. `...T00:00:00Z`
 * without milliseconds). Still returns `null` for an unparseable value.
 */
function parseIso(raw: string | null): string | null {
  if (!raw) return null;
  const parsed = Date.parse(raw);
  return Number.isNaN(parsed) ? null : new Date(parsed).toISOString();
}

/**
 * Is `v` one of the interval values the Historian UI's `IntervalOption`
 * accepts? Exported so `HistorianContext.seedFromUrl` can apply the SAME
 * vocabulary check to the legacy form's `interval` field instead of retyping
 * it — the legacy form's own `VALID_INTERVALS` (`1m`/`5m`/...) is a DIFFERENT,
 * older vocabulary and is not a safe substitute.
 */
export function isInterval(v: string | null): v is IntervalOption {
  if (v === 'auto') return true;
  return (EXPLICIT_INTERVALS as readonly string[]).includes(v ?? '');
}

/**
 * Parse a /historian deep-link's query string into a seed. Returns null when
 * `site` is absent (nothing meaningful to seed). Every other field is
 * validated independently — a bad field is dropped, not fatal.
 */
export function parseHistorianUrlParams(sp: URLSearchParams): HistorianSeed | null {
  const siteId = sp.get('site');
  if (!siteId) return null;

  const rawInterval = sp.get('interval');
  const interval = rawInterval && VALID_INTERVALS.includes(rawInterval) ? rawInterval : null;

  return {
    siteId,
    metrics: parseCsv(sp.get('metrics')),
    deviceIds: parseCsv(sp.get('device_ids')),
    assetType: sp.get('asset_type') ?? '',
    startIso: parseIso(sp.get('start')),
    endIso: parseIso(sp.get('end')),
    interval,
  };
}

/**
 * Parse the `series=`/`q=` deep-link form. Returns null when there is
 * neither a `series` entry nor a `q` — nothing meaningful to seed from.
 *
 * Each `series` value is `siteId:deviceId:metric`; a malformed entry (wrong
 * segment count, or an empty segment) is dropped rather than failing the
 * whole parse, matching the legacy form's per-field tolerance.
 */
export function parseHistorianSeriesUrl(sp: URLSearchParams): HistorianSeriesSeed | null {
  const series = sp
    .getAll('series')
    .map((s) => s.split(':'))
    .filter((p) => p.length === 3 && p.every(Boolean))
    .map(([siteId, deviceId, metric]) => ({ siteId, deviceId, metric }));
  const queryId = sp.get('q');
  if (series.length === 0 && !queryId) return null;

  const rawInterval = sp.get('interval');
  return {
    series,
    startIso: parseIso(sp.get('start')),
    endIso: parseIso(sp.get('end')),
    interval: isInterval(rawInterval) ? rawInterval : null,
    queryId,
  };
}

/**
 * Build the query string for the current selection — the production
 * generator of the `series=`/`q=` form above. Always emits canonical
 * `Date.toISOString()` boundaries, which is why `parseIso` canonicalizing on
 * the way back in is a no-op for anything this function wrote.
 */
export function writeHistorianUrl(s: {
  series: Array<{ siteId: string; deviceId: string; metric: string }>;
  start: Date;
  end: Date;
  interval: IntervalOption;
  queryId?: string | null;
}): string {
  const sp = new URLSearchParams();
  for (const v of s.series) sp.append('series', `${v.siteId}:${v.deviceId}:${v.metric}`);
  sp.set('start', s.start.toISOString());
  sp.set('end', s.end.toISOString());
  sp.set('interval', s.interval);
  if (s.queryId) sp.set('q', s.queryId);
  return sp.toString();
}
